<!--
  SupportAttachmentsPanel.svelte — every file in a ticket's thread, in one list.

  A long thread buries the log someone sent on day one under thirty replies;
  this lists every attachment newest first, with who sent it and when, so it is
  one click away whatever the thread's length. It is built from the messages the
  ticket already carries (`collectTicketAttachments`), which means no request of
  its own and exactly the reader's view: a customer's thread holds no internal
  notes, so neither does their list. At the desk, a file that came in on a note
  carries the same amber "Internal note" tag the thread uses, so an agent never
  forwards one to the customer thinking it was theirs.

  Images show the server's thumbnail and open the preview, exactly as in the
  thread (`SupportImageAttachment`); other files are a download button. The
  panel is absent entirely when the thread has no attachments — an empty box in
  the sidebar would only push the cards below it down.

  Given `ondelete` (the desk passes it), each entry also offers a confirmed
  delete — the one place every file on the ticket is listed, so the one place to
  erase one. The deletion is permanent, which the confirmation says.
-->
<script lang="ts">
	import { Spinner } from 'flowbite-svelte';
	import ConfirmButton from './ConfirmButton.svelte';
	import Panel from './Panel.svelte';
	import RelativeTime from './RelativeTime.svelte';
	import SupportImageAttachment from './SupportImageAttachment.svelte';
	import { downloadSupportAttachment } from '../AppNetwork';
	import type { SupportAttachment, SupportMessage } from '../AppTypes';
	import {
		collectTicketAttachments,
		formatFileSize,
		isPreviewableImage,
		type SupportAudience
	} from '../support/supportFormat';
	import { toastError } from '../toast';
	import { t, t2 } from '../i18n';

	interface Props {
		/** The ticket's thread, as the reader sees it. */
		messages: SupportMessage[];
		/** Which side of the desk is reading — the note tag is shown to agents only. */
		audience?: SupportAudience;
		/** Extra classes on the panel. */
		class?: string;
		/** Erases an attachment; offered on each entry only when given. The caller reports failures. */
		ondelete?: (_attachment: SupportAttachment) => Promise<void>;
	}

	let { messages, audience = 'customer', class: className = '', ondelete }: Props = $props();

	/** Every attachment, newest first, with the message that carried it. */
	let entries = $derived(collectTicketAttachments(messages));

	/** Attachments whose download is in flight, so each shows its own spinner. */
	let downloading: string[] = $state([]);

	/** Attachments whose deletion is in flight; the entry stays until the ticket comes back without it. */
	let deleting: string[] = $state([]);

	/** Deletes one attachment, keeping its button busy meanwhile. */
	async function remove(attachment: SupportAttachment): Promise<void> {
		deleting = [...deleting, attachment.attachmentUUID];
		/** Reporting a failure is the caller's; here it only means the button comes back for a retry. */
		try {
			await ondelete?.(attachment);
		} catch {
			/* Already reported by the caller. */
		} finally {
			deleting = deleting.filter((uuid) => uuid !== attachment.attachmentUUID);
		}
	}

	/** Downloads one attachment through the authenticated fetch, then saves it. */
	async function download(attachment: SupportAttachment): Promise<void> {
		downloading = [...downloading, attachment.attachmentUUID];
		/** The request carries the JWT as a header; a plain link would arrive unauthenticated. */
		try {
			await downloadSupportAttachment(attachment);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to download the attachment'));
		} finally {
			downloading = downloading.filter((uuid) => uuid !== attachment.attachmentUUID);
		}
	}
</script>

{#if entries.length > 0}
	<Panel title={$t('Attachments')} faIcon="paperclip" compact class={className}>
		{#snippet titleSnippet()}
			<span class="text-default rounded-full bg-gray-100 px-2 text-xs font-medium dark:bg-gray-700"
				>{entries.length}</span
			>
		{/snippet}
		<ul class="space-y-3 text-sm" aria-label={$t('Ticket attachments')}>
			{#each entries as { attachment, message } (attachment.attachmentUUID)}
				<li class="flex items-center gap-3" data-testid="ticket-attachment">
					{#if isPreviewableImage(attachment.contentType)}
						<SupportImageAttachment {attachment} ondownload={download} compact />
					{:else}
						<button
							type="button"
							class="bg-depth depth-hover flex h-12 w-12 shrink-0 items-center justify-center rounded-lg"
							disabled={downloading.includes(attachment.attachmentUUID)}
							onclick={() => download(attachment)}
							title={$t('Download {{name}}', { name: attachment.filename })}
							aria-label={$t('Download {{name}}', { name: attachment.filename })}
						>
							{#if downloading.includes(attachment.attachmentUUID)}
								<Spinner size="4" />
							{:else}
								<i class="fa-solid fa-file-arrow-down"></i>
							{/if}
						</button>
					{/if}
					<!-- One line each, the full name on hover: a long screenshot name
					     wrapped over three lines made the list read as a wall of text. -->
					<div class="min-w-0 flex-1 space-y-0.5">
						<button
							type="button"
							class="hover:text-primary-600 dark:hover:text-primary-400 block w-full truncate text-left font-medium"
							onclick={() => download(attachment)}
							title={attachment.filename}
						>
							{attachment.filename}
						</button>
						<p class="text-default truncate text-xs">
							{formatFileSize(attachment.size)} · {message.author?.name ?? $t('System')} ·
							<RelativeTime date={attachment.created} />
						</p>
						{#if audience === 'agent' && message.kind === 'note'}
							<span
								class="inline-block rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-500/30 dark:text-amber-100"
							>
								<i class="fa-solid fa-lock me-1"></i>{$t('Internal note')}
							</span>
						{/if}
					</div>
					{#if ondelete}
						<ConfirmButton
							size="xs"
							color="alternative"
							class="!p-1.5 shrink-0"
							disabled={deleting.includes(attachment.attachmentUUID)}
							confirmText={$t('Delete {{name}}? The file is erased for good and cannot be restored.', {
								name: attachment.filename
							})}
							onconfirm={() => remove(attachment)}
						>
							{#if deleting.includes(attachment.attachmentUUID)}
								<Spinner size="4" />
							{:else}
								<i class="fa-solid fa-trash-can"></i>
							{/if}
							<span class="sr-only">{$t('Delete {{name}}', { name: attachment.filename })}</span>
						</ConfirmButton>
					{/if}
				</li>
			{/each}
		</ul>
	</Panel>
{/if}
